import { DRILL_UPCOMING_DAYS } from '@felix/domain';
import { SegmentedControl } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendarClock,
  IconCalendarEvent,
  IconChartPie,
  IconCircleCheck,
  IconClipboardList,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { useShell } from '../../shell-context.js';
import table from '../pms/Pms.module.css';
import tiles from '../pms/Postponements.module.css';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { DrillFindingStatusBadge, drillRecordPath } from './DrillFindings.js';
import classes from './DrillPlanner.module.css';
import {
  DrillCell,
  DrillFilters,
  DrillStatusBadge,
  useDrillFilter,
  usePlannerData,
} from './DrillPlanParts.js';
import type { DrillFinding, DrillFindings } from './finding-types.js';
import type { DrillPlanSource } from './plan-source.js';
import type { DrillPlan, DrillPlanner } from './plan-types.js';
import {
  type DrillPeriod,
  filterPlans,
  fleetComparison,
  kpisOf,
  periodRange,
  todayOf,
} from './plan-view.js';

export interface DrillDashboardScreenProps {
  source: DrillPlanSource;
}

const PERIODS: readonly DrillPeriod[] = ['month', 'quarter', 'year'];
const PLANNER = '/operations/drills/planner';

/**
 * Operations ▸ Drills ▸ Dashboard (ADR 0128 Faz 2 Ek; ortağın belgesi §3): dönemin KPI'ları
 * (Planned, Completed, Upcoming, Overdue, completion rate), geciken ve yaklaşan tatbikatlar, son
 * tamamlananlar; ofiste filo karşılaştırması. Kartlar Planner'ı ilgili süzgeçle açar.
 */
export function DrillDashboardScreen({ source }: DrillDashboardScreenProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillPlan.dashboard.${key}`, values);
  const filterState = useDrillFilter();
  const vesselFilter = source.scope === 'office' ? filterState.vesselId : null;
  const { state, reload } = usePlannerData(source, vesselFilter);
  const findings = useDrillFindings(source, vesselFilter);
  const header = (
    <PageHeader
      title={t('menu.operationsDrillsDashboard')}
      description={k(source.scope === 'office' ? 'officeDescription' : 'description')}
    />
  );
  if (state.kind === 'loading') {
    return (
      <div className={table.screen}>
        {header}
        <TableSkeleton columns={5} label={t('menu.operationsDrillsDashboard')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={table.screen}>
        {header}
        <Panel>
          <LoadError code={state.code} title={k('error')} onRetry={() => void reload()} />
        </Panel>
      </div>
    );
  }

  return (
    <DrillDashboardBody
      data={state.data}
      filterState={filterState}
      header={header}
      findings={findings}
    />
  );
}

/** Açık bulgular (Faz 4 Ek): kaynak vermiyorsa ya da yüklenemezse kart çizilmez. */
function useDrillFindings(source: DrillPlanSource, vesselId: string | null) {
  const [findings, setFindings] = useState<DrillFindings | null>(null);
  useEffect(() => {
    let live = true;
    if (!source.findings) return;
    source.findings(vesselId).then(
      (data) => {
        if (live) setFindings(data);
      },
      () => {
        if (live) setFindings(null);
      },
    );
    return () => {
      live = false;
    };
  }, [source, vesselId]);
  return findings;
}

/** Panonun gövdesi (yüklenmiş veriyle): dönem, süzgeç, KPI kartları, listeler, filo. */
export function DrillDashboardBody({
  data: ready,
  filterState,
  header,
  findings,
}: {
  data: DrillPlanner;
  filterState: ReturnType<typeof useDrillFilter>;
  header?: ReactNode;
  findings?: DrillFindings | null;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillPlan.dashboard.${key}`, values);
  const shell = useShell();
  const { query, setQuery, vesselId } = filterState;
  const period: DrillPeriod = PERIODS.find((p) => p === query.get('period')) ?? 'month';
  const filter = useMemo(
    () => ({ ...filterState.filter, from: null, to: null }),
    [filterState.filter],
  );
  const plans = useMemo(() => filterPlans(ready.plans, filter), [ready.plans, filter]);
  const today = todayOf(ready.vessels);
  const range = periodRange(period, today);
  const kpis = kpisOf(plans, { ...range, today });
  const fleet = ready.side === 'office' && vesselId === null;
  const open = (status: string | null) => {
    if (!shell) return;
    const params = new URLSearchParams();
    if (vesselId) params.set('vessel', vesselId);
    if (status) params.set('status', status);
    const search = params.toString();
    shell.navigate(search ? `${PLANNER}?${search}` : PLANNER);
  };
  const tile = (node: ReactNode, status: string | null, label: string) =>
    shell ? (
      <button
        type="button"
        className={tiles.tileButton}
        aria-label={k('openPlanner', { label })}
        onClick={() => open(status)}
      >
        {node}
      </button>
    ) : (
      node
    );
  const rate = kpis.completionRate.percent;
  const soon = addDays(today, DRILL_UPCOMING_DAYS);
  const attention = plans
    .filter(
      (p) =>
        p.state === 'active' &&
        (p.status === 'overdue' || p.status === 'postponed' || p.due.nextDueOn <= soon),
    )
    .sort((a, b) => a.due.nextDueOn.localeCompare(b.due.nextDueOn))
    .slice(0, 10);
  const openFindings = (findings?.findings ?? []).filter(
    (f) =>
      (f.status === 'open' || f.status === 'in_progress') &&
      (vesselId === null || f.vesselId === vesselId),
  );
  const overdueFindings = openFindings.filter((f) => f.overdue).length;
  const recent = plans
    .flatMap((p) =>
      p.conducts.filter((c) => c.status === 'approved').map((c) => ({ plan: p, conduct: c })),
    )
    .sort((a, b) => b.conduct.conductedOn.localeCompare(a.conduct.conductedOn))
    .slice(0, 8);

  return (
    <div className={table.screen}>
      {header}
      <Panel>
        <div className={classes.bar}>
          <SegmentedControl
            aria-label={k('period')}
            value={period}
            onChange={(value) => setQuery({ period: value === 'month' ? null : value })}
            data={PERIODS.map((p) => ({ value: p, label: k(`periods.${p}`) }))}
          />
          <span className={classes.muted}>
            {formatDay(range.from)} – {formatDay(range.to)}
          </span>
        </div>
        <DrillFilters data={ready} state={filterState} dates={false} />
      </Panel>
      <div className={classes.tiles} data-count={findings ? 6 : 5}>
        {tile(
          <StatTile
            icon={<IconCalendarEvent size={20} />}
            label={k('kpi.planned')}
            value={kpis.planned}
            wrapLabel
          />,
          null,
          k('kpi.planned'),
        )}
        {tile(
          <StatTile
            icon={<IconCircleCheck size={20} />}
            label={k('kpi.completed')}
            value={kpis.completed}
            tone="success"
            wrapLabel
          />,
          null,
          k('kpi.completed'),
        )}
        {tile(
          <StatTile
            icon={<IconCalendarClock size={20} />}
            label={k('kpi.upcoming')}
            value={kpis.upcoming}
            tone="warning"
            wrapLabel
          />,
          'due,planned',
          k('kpi.upcoming'),
        )}
        {tile(
          <StatTile
            icon={<IconAlertTriangle size={20} />}
            label={k('kpi.overdue')}
            value={kpis.overdue}
            tone="danger"
            wrapLabel
          />,
          'overdue',
          k('kpi.overdue'),
        )}
        <StatTile
          icon={<IconChartPie size={20} />}
          label={k('kpi.rate')}
          value={rate === null ? '—' : `${rate}%`}
          tone={
            rate === null ? 'default' : rate >= 90 ? 'success' : rate >= 70 ? 'warning' : 'danger'
          }
          wrapLabel
        />
        {findings ? (
          <StatTile
            icon={<IconClipboardList size={20} />}
            label={t('drillFinding.dashboard.kpi')}
            value={openFindings.length}
            tone={overdueFindings > 0 ? 'danger' : openFindings.length > 0 ? 'warning' : 'success'}
            wrapLabel
          />
        ) : null}
      </div>
      <p className={classes.muted}>
        {k('rateNote', {
          onTime: kpis.completionRate.onTime,
          counted: kpis.completionRate.counted,
          postponed: kpis.postponed,
        })}
      </p>
      <div className={classes.grid2}>
        <Panel title={k('attention')}>
          {attention.length === 0 ? (
            <EmptyState icon={<IconCircleCheck size={22} />} title={k('noAttention')} />
          ) : (
            <AttentionTable plans={attention} fleet={fleet} onOpen={(p) => openPlan(shell, p)} />
          )}
        </Panel>
        <Panel title={k('recent')}>
          {recent.length === 0 ? (
            <EmptyState icon={<IconCalendarEvent size={22} />} title={k('noRecent')} />
          ) : (
            <div className={table.tableBox}>
              <table className={table.table} data-cards>
                <thead>
                  <tr>
                    <th scope="col">{t('drillPlan.columns.date')}</th>
                    <th scope="col">{t('drillPlan.columns.drill')}</th>
                    {fleet ? <th scope="col">{t('drillPlan.columns.vessel')}</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {recent.map(({ plan, conduct }) => (
                    <tr key={conduct.id}>
                      <td data-label={t('drillPlan.columns.date')}>
                        {formatDay(conduct.conductedOn)}
                      </td>
                      <td data-label={t('drillPlan.columns.drill')}>
                        <DrillCell plan={plan} />
                      </td>
                      {fleet ? (
                        <td data-label={t('drillPlan.columns.vessel')}>{plan.vesselName ?? '—'}</td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
      {findings ? (
        <OpenFindingsPanel findings={openFindings} overdue={overdueFindings} fleet={fleet} />
      ) : null}
      {fleet ? <FleetPanel plans={plans} data={ready} range={range} /> : null}
    </div>
  );
}

/** Açık bulgular ve düzeltici faaliyetler (belge §10): gecikmiş önce, sonra hedef tarih. */
function OpenFindingsPanel({
  findings,
  overdue,
  fleet,
}: {
  findings: DrillFinding[];
  overdue: number;
  fleet: boolean;
}) {
  const { t } = useTranslation();
  const shell = useShell();
  const c = (key: string) => t(`drillFinding.columns.${key}`);
  const sorted = [...findings]
    .sort(
      (a, b) =>
        Number(b.overdue) - Number(a.overdue) ||
        (a.dueOn ?? '9999').localeCompare(b.dueOn ?? '9999') ||
        a.findingNo.localeCompare(b.findingNo),
    )
    .slice(0, 12);
  const title = t('drillFinding.dashboard.panel');
  return (
    <Panel
      title={
        overdue > 0
          ? `${title} · ${t('drillFinding.dashboard.overdue', { count: overdue })}`
          : title
      }
    >
      {sorted.length === 0 ? (
        <EmptyState icon={<IconCircleCheck size={22} />} title={t('drillFinding.dashboard.none')} />
      ) : (
        <div className={table.tableBox}>
          <table className={table.table} data-cards data-testid="open-findings">
            <thead>
              <tr>
                <th scope="col">{c('no')}</th>
                {fleet ? <th scope="col">{c('vessel')}</th> : null}
                <th scope="col">{c('type')}</th>
                <th scope="col">{c('description')}</th>
                <th scope="col">{c('due')}</th>
                <th scope="col">{c('status')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((finding) => (
                <tr key={finding.id}>
                  <td data-label={c('no')}>
                    <button
                      type="button"
                      className={classes.linkButton}
                      onClick={() =>
                        shell?.navigate(
                          drillRecordPath(finding.recordId, fleet ? finding.vesselId : null),
                        )
                      }
                    >
                      {finding.findingNo}
                    </button>
                    {finding.templateCode ? (
                      <div className={classes.muted}>{finding.templateCode}</div>
                    ) : null}
                  </td>
                  {fleet ? <td data-label={c('vessel')}>{finding.vesselName ?? '—'}</td> : null}
                  <td data-label={c('type')}>{t(`drillFinding.types.${finding.type}`)}</td>
                  <td data-label={c('description')}>{finding.description}</td>
                  <td data-label={c('due')}>{finding.dueOn ? formatDay(finding.dueOn) : '—'}</td>
                  <td data-label={c('status')}>
                    <DrillFindingStatusBadge finding={finding} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function openPlan(shell: ReturnType<typeof useShell>, plan: DrillPlan) {
  if (!shell) return;
  const params = new URLSearchParams({ plan: plan.id });
  if (plan.vesselId) params.set('vessel', plan.vesselId);
  shell.navigate(`${PLANNER}?${params.toString()}`);
}

function addDays(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

function AttentionTable({
  plans,
  fleet,
  onOpen,
}: {
  plans: DrillPlan[];
  fleet: boolean;
  onOpen: (plan: DrillPlan) => void;
}) {
  const { t } = useTranslation();
  const c = (key: string) => t(`drillPlan.columns.${key}`);
  return (
    <div className={table.tableBox}>
      <table className={table.table} data-cards>
        <thead>
          <tr>
            <th scope="col">{c('drill')}</th>
            {fleet ? <th scope="col">{c('vessel')}</th> : null}
            <th scope="col">{c('nextDue')}</th>
            <th scope="col">{c('status')}</th>
          </tr>
        </thead>
        <tbody>
          {plans.map((plan) => (
            <tr key={plan.id}>
              <td data-label={c('drill')}>
                <DrillCell plan={plan} onOpen={() => onOpen(plan)} />
              </td>
              {fleet ? <td data-label={c('vessel')}>{plan.vesselName ?? '—'}</td> : null}
              <td data-label={c('nextDue')}>{formatDay(plan.due.nextDueOn)}</td>
              <td data-label={c('status')}>
                <DrillStatusBadge status={plan.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Ofisin filo karşılaştırması: gemi başına dönemin KPI'ları (geminin kendi günüyle). */
function FleetPanel({
  plans,
  data,
  range,
}: {
  plans: DrillPlan[];
  data: { vessels: Parameters<typeof fleetComparison>[1] };
  range: { from: string; to: string };
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drillPlan.dashboard.${key}`);
  const rows = fleetComparison(plans, data.vessels, range);
  return (
    <Panel title={k('fleet')}>
      <div className={table.tableBox}>
        <table className={table.table} data-cards>
          <thead>
            <tr>
              <th scope="col">{t('drillPlan.columns.vessel')}</th>
              <th scope="col">{k('kpi.planned')}</th>
              <th scope="col">{k('kpi.completed')}</th>
              <th scope="col">{k('kpi.upcoming')}</th>
              <th scope="col">{k('kpi.overdue')}</th>
              <th scope="col">{k('kpi.rate')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ vessel, kpis }) => (
              <tr key={vessel.id}>
                <td data-label={t('drillPlan.columns.vessel')}>{vessel.name}</td>
                <td data-label={k('kpi.planned')} data-numeric>
                  {kpis.planned}
                </td>
                <td data-label={k('kpi.completed')} data-numeric>
                  {kpis.completed}
                </td>
                <td data-label={k('kpi.upcoming')} data-numeric>
                  {kpis.upcoming}
                </td>
                <td data-label={k('kpi.overdue')} data-numeric>
                  {kpis.overdue}
                </td>
                <td data-label={k('kpi.rate')} data-numeric>
                  {kpis.completionRate.percent === null ? '—' : `${kpis.completionRate.percent}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
